<template>
    <LiveLinkFooter
        title="Google Ads Scorecard"
        :created-on="lastUpdated"
        :created-by="createdBy"
        :mode="mode"
        :viewed-by="viewedBy"
        :width="mode === 'live' ? '51.25rem' : '62.75rem'"
        :link-share-loading="linkShareLoading"
        :download-loading="downloadLoading"
        :download-failed="downloadFailed"
        @copy-share-link="copyShareLink"
        @download-scorecard="$emit('clicked-download')"
        @delete-scorecard="$emit('clicked-delete')"
        :is-auto-generated="isAutoGenerated"
    >
        <template #avatar>
            <Avatar
                v-if="accountColour && accountInitials"
                :color="accountColour"
                :width="36"
                :height="36"
                showAccountType
            >
                {{ accountInitials }}
            </Avatar>
            <Skeleton v-else :width="36" :height="36" />
        </template>
    </LiveLinkFooter>

    <!-- Score History Panel -->
    <Panel
        :modelValue="showScoreHistoryPanel"
        @close="showScoreHistoryPanel = false"
        :title="aboveMobile ? (panelTitle ?? '') : 'Score History'"
        :width="884"
        noPadding
    >
        <template #title v-if="aboveMobile">
            <ColorTag color="blue" title="Score History" />
        </template>
        <template #content>
            <div class="panel-content-container">
                <!-- Graph History Overview -->
                <div class="section">
                    <div class="inner">
                        <div class="row-header">
                            <Text as="h5" weight="600">Last 12 Months</Text>
                        </div>
                        <Spacer height="0.75rem" />
                        <ScoreHistoryChart
                            :all-time-high="allTimeHigh"
                            :all-time-low="allTimeLow"
                            :items="scoreHistoryChartItems"
                        />
                    </div>
                </div>
                <!-- Table History Breakdown -->
                <div v-if="scoreHistoryBreakdown" class="section">
                    <div class="inner">
                        <div class="row-header">
                            <Text as="h5" weight="600">{{ scoreHistoryBreakdown.title }}</Text>
                        </div>
                        <div class="history-table">
                            <div
                                v-for="(item, index) in scoreHistoryBreakdown.metrics"
                                :key="item.id"
                            >
                                <div
                                    class="row"
                                    @mouseenter="tableRowHovered = index"
                                    @mouseleave="tableRowHovered = null"
                                    :style="
                                        index === scoreHistoryBreakdown.metrics.length - 1
                                            ? 'border-bottom: none'
                                            : ''
                                    "
                                >
                                    <div class="name-table-cell">
                                        <Text
                                            as="p"
                                            size="f-8"
                                            :color="item.noData ? 'gray' : 'black'"
                                        >
                                            {{ item.name }}
                                        </Text>
                                    </div>
                                    <div class="score-table-cell">
                                        <ScorecardDonut
                                            :score="item.score"
                                            :compact="true"
                                            :width="22"
                                            :strokeWidth="2"
                                            hide-score
                                            :hidden="item.noData ? true : false"
                                        />
                                        <Text
                                            as="p"
                                            size="f-8"
                                            weight="500"
                                            :color="
                                                item.noData ? 'gray' : scoreColor(item.score).name
                                            "
                                        >
                                            <span v-if="item.noData">N/A</span>
                                            <Percent
                                                v-else
                                                :value="item.score / 100"
                                                :decimal-places="0"
                                            />
                                        </Text>
                                    </div>
                                    <transition name="button">
                                        <div
                                            v-if="
                                                mode === 'app' &&
                                                !item.noData &&
                                                tableRowHovered === index
                                            "
                                            class="button-container"
                                        >
                                            <Tooltip
                                                :content="
                                                    item.url === 'isAvailable'
                                                        ? ''
                                                        : item.url === 'isCurrent'
                                                          ? 'Currently viewing'
                                                          : 'Scorecard unavailable'
                                                "
                                                :offset="[0, 10]"
                                                :delay="[100, 20]"
                                            >
                                                <oButton
                                                    color="white"
                                                    size="small"
                                                    @clicked="viewScorecard(item.id)"
                                                    :disabled="
                                                        item.url === 'isCurrent' ||
                                                        item.url === 'isUnavailable'
                                                    "
                                                >
                                                    View Scorecard
                                                </oButton>
                                            </Tooltip>
                                        </div>
                                    </transition>
                                </div>
                            </div>
                            <div
                                v-if="scoreHistoryBreakdown.metrics.length === 1"
                                class="row no-history"
                            >
                                <Text as="p" size="f-8" color="gray">
                                    Score history data will display here
                                </Text>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </Panel>

    <div class="outer-container" ref="outerContainer">
        <div class="page-content-container">
            <div
                :class="['cover-container', { 'not-pdf-render': mode !== 'pdf' }]"
                v-if="shouldShowSection('cover')"
            >
                <div class="edit-cover-container" v-if="mode === 'app'">
                    <oButton color="white" size="medium" @clicked="$emit('clicked-edit-cover')">
                        Edit Cover
                    </oButton>
                </div>
                <Cover
                    :domain-name="accountName"
                    :account-name="accountName"
                    :account-logo-url="coverPageData.accountLogoUrl"
                    :domain-colour="accountColour"
                    :location="coverPageData.location"
                    :website-url="coverPageData.website"
                    :industry="coverPageData.industry"
                    :last-updated="lastUpdated"
                    :is-loading="false"
                    :user-full-name="coverPageData.senderName"
                    :user-email="coverPageData.senderEmail"
                    :user-id="coverPageData.userId"
                    :profile-image-url="coverPageData.senderLogoUrl"
                    @clicked-account-name="$emit('clicked-account-name')"
                    @clicked-sender-name="$emit('clicked-sender-name')"
                    :mode="mode"
                    :is-auto-generated="isAutoGenerated"
                />
            </div>

            <Overview
                v-if="shouldShowSection('overview')"
                :score="overviewScore"
                :scores="scorecardBody.sections"
                :last-updated="lastUpdated"
                :is-loading="false"
                :key-statistics="scorecardBody.keyStatistics"
                :domain-currency="accountInfo.currency"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                @scoreHistoryButtonClicked="showHistoryPanel('overview')"
            />

            <Integrations
                v-if="shouldShowSection(SectionTypes.integrations)"
                :score="sections?.integrations?.score"
                :invalid="sections?.integrations?.invalid"
                :is-loading="false"
                :details="sections?.integrations?.details"
                :domain-name="accountName ?? ''"
                :mode="mode"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.integrations)"
            />

            <ConversionTracking
                v-if="shouldShowSection(SectionTypes.conversionTracking)"
                :score="sections?.conversionTracking.score"
                :invalid="sections?.conversionTracking.invalid"
                :is-loading="false"
                :domain-currency="accountInfo.currency"
                :domain-name="accountName"
                :details="sections?.conversionTracking.details"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.conversionTracking)"
            />

            <Disapprovals
                v-if="shouldShowSection(SectionTypes.disapprovals)"
                :score="sections?.disapprovals?.score"
                :invalid="sections?.disapprovals?.invalid"
                :is-loading="false"
                :details="sections?.disapprovals?.details"
                :domain-currency="accountInfo.currency"
                :domain-name="accountName ?? ''"
                :domain-id="accountInfo?.accountId"
                :mode="mode"
                :breakdown="breakdowns?.disapprovals"
                :breakdown-loading="loadingBreakdowns.disapprovals"
                @breakdown-requested="requestBreakdown(SectionTypes.disapprovals)"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.disapprovals)"
            />

            <LandingPages
                v-if="shouldShowSection(SectionTypes.landingPages)"
                :score="sections?.landingPages?.score"
                :invalid="sections?.landingPages?.invalid"
                :is-loading="false"
                :details="sections?.landingPages?.details"
                :domain-name="accountName ?? ''"
                :domain-currency="accountInfo.currency"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                :breakdown="breakdowns.landingPages"
                @breakdown-requested="requestBreakdown(SectionTypes.landingPages)"
                :breakdown-loading="loadingBreakdowns.landingPages"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.landingPages)"
            />

            <ProjectedSpend
                v-if="shouldShowSection(SectionTypes.projectedSpend)"
                :score="sections?.projectedSpend.score"
                :invalid="sections?.projectedSpend?.invalid"
                :is-loading="false"
                :domain-currency="accountInfo.currency"
                :domain-name="accountName"
                :details="sections?.projectedSpend?.details"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.projectedSpend)"
            />

            <BudgetLimitedCampaigns
                v-if="shouldShowSection(SectionTypes.budgetCap)"
                :score="sections?.budgetCap.score"
                :invalid="sections?.budgetCap.invalid"
                :is-loading="false"
                :domain-currency="accountInfo.currency"
                :domain-name="accountName"
                :details="sections?.budgetCap.details"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.budgetCap)"
            />

            <SpendAllocation
                v-if="shouldShowSection(SectionTypes.spendAllocation)"
                :score="sections?.spendAllocation.score"
                :invalid="sections?.spendAllocation?.invalid"
                :is-loading="false"
                :details="sections?.spendAllocation.details"
                :domain-currency="accountInfo.currency"
                :domain-name="accountName"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                :breakdown="breakdowns.spendAllocation"
                :breakdown-loading="loadingBreakdowns.spendAllocation"
                @breakdown-requested="requestBreakdown(SectionTypes.spendAllocation)"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.spendAllocation)"
            />

            <QualityScore
                v-if="shouldShowSection(SectionTypes.qs)"
                :score="scorecardBody.sections?.qs.score"
                :invalid="scorecardBody.sections?.qs.invalid"
                :details="scorecardBody.sections?.qs.details"
                :is-loading="false"
                :domain-currency="accountInfo.currency"
                :domain-name="accountInfo.name"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                :breakdown="breakdowns.qs"
                :breakdown-loading="loadingBreakdowns.qs"
                @breakdown-requested="requestBreakdown(SectionTypes.qs)"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.qs)"
            />

            <Rsa
                v-if="shouldShowSection(SectionTypes.rsa)"
                :score="sections?.rsa?.score"
                :invalid="sections?.rsa?.invalid"
                :is-loading="false"
                :account-info="accountInfo"
                :details="sections?.rsa.details"
                :breakdown="breakdowns.rsa"
                :domain-currency="accountInfo.currency"
                :domain-name="accountName"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.rsa)"
                @breakdown-requested="requestBreakdown(SectionTypes.rsa)"
            />

            <AdExtensionCoverage
                v-if="shouldShowSection(SectionTypes.adExtension)"
                :score="sections?.adExtension.score"
                :invalid="sections?.adExtension.invalid"
                :domain-currency="accountInfo.currency"
                :domain-name="accountName"
                :is-loading="false"
                :details="sections?.adExtension.details"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                :breakdown="breakdowns.adExtension"
                :breakdown-loading="loadingBreakdowns.adExtension"
                @breakdown-requested="requestBreakdown(SectionTypes.adExtension)"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.adExtension)"
            />

            <MatchTypeBreakdown
                v-if="shouldShowSection(SectionTypes.matchtype)"
                :score="sections?.matchtype.score"
                :invalid="sections?.matchtype.invalid"
                :domain-currency="accountInfo.currency"
                :domain-name="accountName"
                :is-loading="false"
                :details="sections?.matchtype.details"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.matchtype)"
            />

            <SearchTermCoverage
                v-if="shouldShowSection(SectionTypes.sqr)"
                :score="scorecardBody.sections.sqr.score"
                :invalid="scorecardBody.sections.sqr.invalid"
                :is-loading="false"
                :details="scorecardBody.sections.sqr.details"
                :domain-currency="accountInfo.currency"
                :domain-name="accountName"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.sqr)"
            />

            <SearchTermAlignment
                v-if="shouldShowSection(SectionTypes.searchTermAlignment)"
                :score="sections?.searchTermAlignment?.score"
                :invalid="sections?.searchTermAlignment?.invalid"
                :is-loading="false"
                :details="sections?.searchTermAlignment?.details"
                :domain-name="accountName ?? ''"
                :domain-currency="accountInfo.currency"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                :breakdown="breakdowns.searchTermAlignment"
                :breakdown-loading="loadingBreakdowns.searchTermAlignment"
                @breakdown-requested="requestBreakdown(SectionTypes.searchTermAlignment)"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.searchTermAlignment)"
            />

            <PerformanceMax
                v-if="shouldShowSection(SectionTypes.performanceMax)"
                :score="sections?.performanceMax?.score"
                :invalid="sections?.performanceMax?.invalid"
                :is-loading="false"
                :details="sections?.performanceMax?.details"
                :domain-currency="accountInfo.currency"
                :domain-name="accountName"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                :breakdown="breakdowns.performanceMax"
                :breakdown-loading="loadingBreakdowns.performanceMax"
                @breakdown-requested="requestBreakdown(SectionTypes.performanceMax)"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.performanceMax)"
            />

            <ProductPerformance
                v-if="shouldShowSection(SectionTypes.productPerformance)"
                :score="sections?.productPerformance?.score"
                :invalid="sections?.productPerformance?.invalid"
                :is-loading="false"
                :details="sections?.productPerformance.details"
                :domain-currency="accountInfo.currency"
                :domain-name="accountName"
                :is-using-cpa="isUsingCpa"
                :mode="mode"
                :breakdown="breakdowns.productPerformance"
                :breakdown-loading="loadingBreakdowns.productPerformance"
                @breakdown-requested="requestBreakdown(SectionTypes.productPerformance)"
                @scoreHistoryButtonClicked="showHistoryPanel(SectionTypes.productPerformance)"
            />
        </div>
    </div>
    <!-- Used for trimming PDF pages during final PDF assembly -->
    <p v-if="mode === 'pdf'" style="color: white; text-align: center; font-family: arial">
        [CONTENTHEIGHT:{{ contentHeight }}]
    </p>
</template>

<script lang="ts">
import { PropType, computed, defineComponent, ref } from 'vue'
import { useRouter } from 'vue-router'
import compact from 'lodash-es/compact'
import useMediaQuery from '@/composition/global/useMediaQuery'

import { Scorecard, Targets } from '@opteo/types'

import ImageUploader from '@/components/global/ImageUploader.vue'
import {
    Text,
    Spacer,
    ExpandableSidebarButton,
    ExpandableSidebar,
    oForm,
    oInput,
    oButton,
    Label,
    Spinner,
    CrossIcon,
    ProfileImage,
    Modal,
    Avatar,
    Panel,
    oTable,
    Delta,
    Percent,
    ColorTag,
    Tooltip,
} from '@opteo/components-next'

import ScorecardDomainImage from '@/components/global/ScorecardDomainImage.vue'
import Skeleton from '@/components/util/Skeleton.vue'

import SearchTermCoverage from '@/components/scorecard/SearchTermCoverage.vue'
import AdExtensionCoverage from '@/components/scorecard/AdExtensionCoverage.vue'
import Cover from '@/components/scorecard/Cover.vue'
import Overview from '@/components/scorecard/Overview.vue'
import ScoreHistoryChart from '@/components/scorecard/ScoreHistoryChart.vue'
import ScorecardDonut from '@/components/scorecard/ScorecardDonut.vue'
import QualityScore from '@/components/scorecard/QualityScore.vue'
import ProjectedSpend from '@/components/scorecard/ProjectedSpend.vue'
import BudgetLimitedCampaigns from '@/components/scorecard/BudgetLimitedCampaigns.vue'
import SpendAllocation from '@/components/scorecard/SpendAllocation.vue'
import MatchTypeBreakdown from '@/components/scorecard/MatchTypeBreakdown.vue'
import ConversionTracking from '@/components/scorecard/ConversionTracking.vue'
import PerformanceMax from '@/components/scorecard/PerformanceMax.vue'
import Disapprovals from '@/components/scorecard/Disapprovals.vue'
import ProductPerformance from '@/components/scorecard/ProductPerformance.vue'
import LandingPages from '@/components/scorecard/LandingPages.vue'
import Integrations from '@/components/scorecard/Integrations.vue'
import Rsa from '@/components/scorecard/Rsa/Rsa.vue'
import SearchTermAlignment from '@/components/scorecard/SearchTermAlignment.vue'
import LiveLinkFooter from '@/components/global/LiveLinkFooter.vue'

import { scoreColor } from '@/composition/toolkit/scorecard/utils'

import { useElementSize } from '@vueuse/core'
import { openInNewTab } from '@/lib/globalUtils'
import {
    extractSenderInfo,
    getAccountInitials,
    getCreationMonth,
} from '@/components/scorecard/utils'

type UrlAvailability = 'isAvailable' | 'isUnavailable' | 'isCurrent'

export default defineComponent({
    name: 'ScorecardContent',
    components: {
        ScorecardDomainImage,
        ImageUploader,
        Spacer,
        Cover,
        Overview,
        SearchTermCoverage,
        QualityScore,
        ProjectedSpend,
        BudgetLimitedCampaigns,
        SpendAllocation,
        MatchTypeBreakdown,
        AdExtensionCoverage,
        ConversionTracking,
        PerformanceMax,
        Disapprovals,
        Integrations,
        ProductPerformance,
        Rsa,
        LandingPages,
        SearchTermAlignment,
        ExpandableSidebarButton,
        ExpandableSidebar,
        Text,
        oForm,
        oInput,
        oButton,
        Label,
        Skeleton,
        Spinner,
        CrossIcon,
        ScorecardDonut,
        ProfileImage,
        Modal,
        Avatar,
        Panel,
        oTable,
        Delta,
        Percent,
        ColorTag,
        Tooltip,
        LiveLinkFooter,
        ScoreHistoryChart,
    },
    props: {
        scorecardBody: {
            type: Object as PropType<Scorecard.ScorecardBody>,
            required: true,
        },
        accountInfo: {
            type: Object as PropType<Scorecard.ScorecardAccountInfo>,
            required: true,
        },
        createdAt: {
            type: String as PropType<string>,
            required: true,
        },
        overviewScore: {
            type: Number as PropType<number>,
            required: true,
        },
        mode: {
            type: String as PropType<'live' | 'pdf' | 'app'>,
            required: true,
        },
        showSection: {
            type: String as PropType<Scorecard.SectionTypeIncCover>,
            required: false,
        },
        linkShareLoading: {
            type: Boolean as PropType<boolean>,
            required: false,
            default: false,
        },
        downloadLoading: {
            type: Boolean as PropType<boolean>,
            required: false,
            default: false,
        },
        downloadFailed: {
            type: Boolean as PropType<boolean>,
            required: false,
            default: false,
        },
        viewedBy: {
            type: Array as PropType<Scorecard.LiveViewer[]>,
            required: false,
            default: [],
        },
        scorecardHistory: {
            type: Array as PropType<Scorecard.ScorecardListItem[]>,
            required: false,
            default: [],
        },
        isAutoGenerated: {
            type: Boolean,
            required: false,
            default: false,
        },
        breakdowns: {
            type: Object as PropType<Partial<Scorecard.ScorecardBreakdowns>>,
            required: false,
            default: {},
        },
        loadingBreakdowns: {
            type: Object as PropType<Partial<Record<keyof Scorecard.ScorecardBreakdowns, boolean>>>,
            required: false,
            default: {},
        },
    },
    emits: [
        'copy-share-link',
        'clicked-account-name',
        'clicked-sender-name',
        'clicked-edit-cover',
        'clicked-download',
        'clicked-delete',
        'breakdown-requested',
    ],
    setup(props, { emit }) {
        const isUsingCpa = computed(
            () => props.scorecardBody.performanceMode === Targets.PerformanceMode.CPA
        )
        const router = useRouter()
        // scorecardId is only available on app mode
        const scorecardId = router.currentRoute.value.params.scorecardId

        const { aboveMobile } = useMediaQuery()

        const tableRowHovered = ref<number | null>(null)
        const panelSection = ref<Scorecard.SectionType | 'overview' | undefined>()
        const lastUpdated = computed(() => props.createdAt)

        const createdBy = computed(() => {
            const { senderName, senderEmail, userId, senderLogoUrl } = extractSenderInfo(
                props.scorecardBody
            )

            return {
                userFullName: senderName || senderEmail,
                userId: userId ? userId.toString() : '',
                profileImageUrl: senderLogoUrl || '',
            }
        })

        const coverPageData = computed(() => props.scorecardBody.coverPageData)

        const accountName = computed(() => coverPageData.value?.accountName ?? undefined)
        const accountInitials = computed(() => getAccountInitials(accountName.value))

        const accountColour = computed(() => props.accountInfo?.colourPreference)

        const sections = computed(() => props.scorecardBody.sections)

        const shouldShowSection = (section: Scorecard.SectionTypeIncCover) => {
            const sectionsToAlwaysShow = ['cover', 'overview']
            // Don't show it if it's missing from the scorecard body.
            // Could happen if it's an old scorecard with fewer sections,
            // or if the sections were filtered out because invalid.
            if (
                !Object.keys(props.scorecardBody.sections).includes(section) &&
                !sectionsToAlwaysShow.includes(section)
            ) {
                return false
            }
            // If a single section is specified, only show that one. Happens when generating PDFs.
            if (props.showSection) {
                return props.showSection === section
            }

            return true
        }

        /**
         * Panels
         */

        const showScoreHistoryPanel = ref(false)

        const scoreHistoryBreakdown = computed(() => {
            if (!panelSection?.value) {
                return
            }

            const handleUrl = (currentSectionId: string | number): UrlAvailability => {
                return scorecardId === currentSectionId ? 'isCurrent' : 'isAvailable'
            }

            let metrics
            if (panelSection.value === 'overview') {
                metrics = compact(
                    props.scorecardHistory.map(section => {
                        return {
                            id: section.id,
                            name: getCreationMonth(section.createdAt),
                            score: section.overallScore,
                            noData: section?.noData ?? false,
                            url: handleUrl(section.id),
                        }
                    })
                )
            } else {
                metrics = props.scorecardHistory.flatMap(monthData => {
                    return compact(
                        monthData.sections.map(section => {
                            if (section.type === panelSection.value) {
                                return {
                                    id: monthData.id,
                                    name: getCreationMonth(monthData.createdAt),
                                    score: section.score,
                                    noData: section?.noData ?? false,
                                    url: handleUrl(monthData.id),
                                }
                            }
                        })
                    )
                })
            }

            return { id: scorecardId, title: panelTitle.value, metrics }
        })

        // Score History Chart
        const scoreHistoryChartItems = computed(() => {
            const breakdownData =
                scoreHistoryBreakdown.value?.metrics?.map(metric => {
                    return {
                        label: metric.name,
                        score: +(metric?.score?.toFixed() ?? 0),
                        noData: metric?.noData ?? false,
                    }
                }) ?? []

            return breakdownData.slice().reverse()
        })

        // Record High / Low
        const allTimeHigh = computed(() => {
            const monthsWithData = scoreHistoryChartItems?.value?.filter(sc => !sc.noData)
            if (!monthsWithData || !monthsWithData.length) {
                return
            }
            return Math.max(...(monthsWithData?.map(sc => sc?.score ?? 0) ?? []))
        })

        const allTimeLow = computed(() => {
            const monthsWithData = scoreHistoryChartItems?.value?.filter(sc => !sc.noData)
            if (!monthsWithData || !monthsWithData.length) {
                return
            }
            return Math.min(...(monthsWithData?.map(sc => sc?.score ?? 0) ?? []))
        })

        function viewScorecard(id: string | number) {
            showScoreHistoryPanel.value = false

            const { href } = router.resolve({
                params: { ...router.currentRoute.value.params, scorecardId: id },
                query: { section: panelSection.value },
            })

            openInNewTab(href)
        }

        /**
         * Rendering & downloading
         */

        function copyShareLink() {
            emit('copy-share-link')
        }

        const panelTitle = computed(() => {
            if (!panelSection?.value) {
                return
            } else if (panelSection?.value === 'overview') {
                return 'Overall Score'
            }
            return Scorecard.SectionNames[panelSection?.value]
        })

        function showHistoryPanel(section: Scorecard.SectionType | 'overview') {
            if (!section) {
                throw new Error('missing panel section')
            }
            showScoreHistoryPanel.value = true
            panelSection.value = section
        }

        const outerContainer = ref()
        const size = useElementSize(outerContainer)
        const contentHeight = computed(() => size.height)

        const requestBreakdown = (sectionType: Scorecard.SectionTypeWithBreakdown) => {
            emit('breakdown-requested', sectionType)
        }

        return {
            SectionTypes: Scorecard.SectionTypes,
            aboveMobile,
            tableRowHovered,
            isUsingCpa,
            coverPageData,
            lastUpdated,
            createdBy,
            accountColour,
            accountInitials,
            accountName,
            viewScorecard,
            sections,
            shouldShowSection,
            showScoreHistoryPanel,
            panelTitle,
            showHistoryPanel,
            scoreHistoryBreakdown,
            scoreHistoryChartItems,
            allTimeHigh,
            allTimeLow,
            scoreColor,
            copyShareLink,
            outerContainer,
            contentHeight,
            requestBreakdown,
        }
    },
})
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';
@import '@/assets/css/variables.scss';

.outer-container {
    max-width: 67.5rem;
    margin: 0 auto;
}

.page-content-container {
    @include flex;
    @include items-center;
    flex-direction: column;
    gap: 2rem;
    margin-bottom: 10rem;
}

.cover-container {
    @include w-100;
    @include relative;
    display: block;
    overflow: hidden;
}

.cover-container.not-pdf-render {
    @include container;
    border-radius: 2rem;
}

.edit-cover-container {
    @include absolute;
    top: 1.5rem;
    right: 1.5rem;
}
// Scorecard History Panel
.panel-content-container .section {
    border-bottom: 1px solid;
    @include opteo-border;
    @include pa-48;
}
.panel-content-container .section:last-child {
    border-bottom: unset;
}
.panel-content-container .section .inner {
    @include w-100;
    max-width: 25rem;
    margin: 0 auto;
}

.panel-content-container .section .row-header {
    font-size: 1.125rem;
    @include mb-24;
    @include flex;
    @include items-center;
    @include justify-between;
}
.panel-content-container .section .history-table {
    @include container;
    @include br-16;
}
.panel-content-container .section .history-table .row {
    @include flex;
    @include justify-between;
    border-bottom: 1px solid;
    @include opteo-border;
    height: 4rem;
    @include items-center;
    @include relative;
}
.panel-content-container .section .history-table .row.no-history {
    @include flex;
    @include justify-center;
    border-top: 1px solid;
    @include opteo-border;
    border-bottom: unset;
}
.panel-content-container .section .history-table .button-container {
    @include absolute;
    @include flex;
    @include justify-end;
    @include items-center;
    right: 0;
    background: $opteo-white;
    @include pr-20;
    width: 12.5rem;
}
.panel-content-container .section .history-table .name-table-cell {
    @include pl-24;
}
.panel-content-container .section .history-table .score-table-cell {
    @include flex;
    @include items-center;
    gap: 0.5rem;
    @include pr-24;
    width: 92px;
}

// Responsive
@media (max-width: $mq-480-max) {
    .outer-container {
        @include ph-16;
        @include pt-16;
    }
    .page-content-container {
        gap: 1rem;
    }
    .edit-cover-container {
        display: none;
    }
    // Scorecard History Panel
    .panel-content-container {
        box-shadow: unset;
    }
    .panel-content-container .section {
        border-bottom: unset;
        padding: unset;
    }
    .panel-content-container .section:last-child {
        border-bottom: unset;
        padding-top: 2.5rem;
    }
    .panel-content-container .section .inner {
        @include w-100;
        max-width: 25rem;
        margin: 0 auto;
    }
}

@media screen and (min-width: $mq-481-min) and (max-width: $mq-767-max) {
    .outer-container {
        @include ph-32;
        @include pt-64;
    }
    .edit-cover-container {
        display: none;
    }
}

// Button transition
.button-enter-active,
.button-leave-active {
    transition: all 0.3s cubic-bezier(0.19, 1, 0.22, 1);
}
.button-enter-from,
.button-leave-to {
    opacity: 0;
    transform: translateY(8px);
}
</style>
